<template>
  <div v-rect-loading="loading" class="email-template-editor">
    <div class="header">
      <div class="title">{{ $t(`admin.company.onboarding.title.emailTemplate.${isEditMode ? 'edit' : 'add'}`) }}</div>
    </div>
    <div class="content">
      <div class="tip-message">
        <div v-html="$t(`admin.company.onboarding.message.emailTemplateTip.warn`)"></div>
        <table v-html="$t(`admin.company.onboarding.message.emailTemplateTip.description`)"></table>
      </div>
      <el-divider></el-divider>
      <WrappedFrom
        class="form-container"
        ref="formEditor"
        :model="emailTemplate"
        :form-lines="formLines"
      >
      </WrappedFrom>
      <div class="template-editor">
        <quill-editor v-model="emailTemplate.template" ref="quillEditor" :options="quillEditorOption" @change="handleTemplateChange"></quill-editor>
        <div class="test-send">
          <el-button class="blue-btn" @click="handleTestSend">{{ $t('admin.company.onboarding.btn.sendTestEmail') }}</el-button>
        </div>
      </div>
      <div ref="workSpace" class="work-space"></div>
      <el-divider></el-divider>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { addTitleInToolbar } from '@/utils/quillEditor'

const includeKeyList = [
  'margin',
  'padding',
  'border',
  'background',
  'color',
  'fontSize',
  'fontFamily',
  'lineHeight',
  'textAlign',
  'textDecoration',
  'direction',
  'boxSizing',
  'listStyleType'
]

export default {
  async beforeRouteLeave (to, from, next) {
    if (this.templateChanged) {
      let canLeave = true
      canLeave = window.confirm(this.$t('common.message.unSaveChangeConfirm'))
      next(canLeave)
    } else {
      next()
    }
  },
  components: {
    WrappedFrom
  },
  data () {
    return {
      loading: false,
      emailTemplate: {
        id: -1,
        subject: '',
        template: '',
        type: '1',
        scope: 'SATISFACTION'
      },
      initChange: false,
      templateChanged: false,
      i18nPrefix: 'admin.company.postApplicant',
      quillEditorOption: {
        placeholder: this.$t('admin.company.postApplicant.placeholder.emailTemplate'),
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ direction: 'rtl' }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ['link', 'image'],
            ['clean']
          ]
        }
      }
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    form () {
      return this.$refs.formEditor
    },
    formLines () {
      return {
        subject: {
          label: this.$t(`${this.i18nPrefix}.label.emailSubject`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.emailSubject`),
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.emailSubjectRequired`) }
        }
      }
    }
  },
  created () {
    if (this.isEditMode) {
      this.getEmailTemplateData()
    } else {
      this.getEmailTemplateDataById(-1)
    }
  },
  mounted () {
    addTitleInToolbar()
  },
  methods: {
    ...mapActions('postApplicantManagement', ['getEmailTemplateById', 'updateEmailTemplate', 'sendTestEmail']),
    getEmailTemplateData () {
      const emailTemplateId = this.$route.params.emailTemplateId
      this.getEmailTemplateDataById(emailTemplateId)
    },
    async getEmailTemplateDataById (emailTemplateId) {
      this.loading = true
      try {
        const resp = await this.getEmailTemplateById(emailTemplateId)
        if (resp) {
          const emailTemplate = resp.emailTemplate
          if (emailTemplate.template) {
            this.initChange = true
          }
          if (emailTemplateId === -1 && emailTemplate.subject) {
            emailTemplate.subject = null
          }
          this.emailTemplate = emailTemplate
        }
      } finally {
        this.loading = false
        this.$nextTick(() => this.form.clearValidate())
      }
    },
    deepStyleFormat (originChildren, targetChildren) {
      _.forEach(originChildren, (child, index) => {
        const targetChild = targetChildren[index]
        if (!child || !targetChild) {
          return true
        }
        const originStyles = window.getComputedStyle(child, null)
        const targetStyles = window.getComputedStyle(targetChild, null)
        _.forEach(includeKeyList, (key) => {
          const originValue = originStyles[key]
          const targetValue = targetStyles[key]
          if (originValue !== targetValue) {
            targetChild.style[key] = originValue
          }
        })
        if (child.children && child.children.length > 0) {
          this.deepStyleFormat(child.children, targetChild.children)
        }
      })
    },
    handleTemplateChange () {
      if (this.initChange) {
        this.initChange = false
        return
      }
      if (!this.templateChanged) {
        window.onbeforeunload = (event) => {
          event.returnValue = this.$t('common.message.unSaveChangeConfirm')
          return event.returnValue
        }
        this.templateChanged = true
      }
    },
    async validate () {
      let result = false
      result = await this.form.submit()
      if (result) {
        if (!this.emailTemplate.template) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.emailTemplateRequired`))
          result = false
        }
      }
      return result
    },
    handleCancel () {
      this.$router.push('/v-admin/post-applicant-management/email-template/list')
    },
    async handleTestSend () {
      const valid = await this.validate()
      if (!valid) {
        return
      }
      const workSpace = this.$refs.workSpace
      const quillEditor = this.$refs.quillEditor
      const editor = quillEditor.$el.querySelector('.ql-editor')
      workSpace.innerHTML = this.emailTemplate.template
      this.deepStyleFormat(editor.children, workSpace.children)
      const template = workSpace.innerHTML

      const emailTemplate = {
        ...this.emailTemplate,
        template
      }
      this.loading = true
      try {
        const resp = await this.sendTestEmail(emailTemplate)
        if (resp) {
          this.$message.success(this.$t('admin.company.postApplicant.message.testEmailHasSent'))
        }
      } finally {
        this.loading = false
      }
    },
    async handleSave () {
      const valid = await this.validate()
      if (!valid) {
        return
      }
      const workSpace = this.$refs.workSpace
      const quillEditor = this.$refs.quillEditor
      const editor = quillEditor.$el.querySelector('.ql-editor')
      workSpace.innerHTML = this.emailTemplate.template
      this.deepStyleFormat(editor.children, workSpace.children)
      const template = workSpace.innerHTML

      const emailTemplate = {
        ...this.emailTemplate,
        template: template
      }
      this.loading = true
      try {
        const resp = await this.updateEmailTemplate(emailTemplate)
        if (resp) {
          window.onbeforeunload = null
          this.templateChanged = false
          this.$router.push('/v-admin/post-applicant-management/email-template')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.email-template-editor {
  .header {
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 770px;
    margin: 0 auto;

    .tip-message {
      ::v-deep {
        margin-top: 20px;
        padding: 5px;
        border: 1px solid rgb(242, 205, 0);
        background: rgb(251, 243, 141);
        color: #808080;

        .strong {
          font-size: 18px;
          font-weight: bold;
        }

        table {
          margin: 10px auto 0;
          text-align: center;

          td {
            padding: 5px 10px;
          }
        }
      }
    }

    .form-container {
      ::v-deep {
        .el-form-item__label-wrap {
          .el-form-item__label {
            .label-inner {
              margin-top: 0;
              line-height: inherit;
            }
          }
        }
      }
    }

    .template-editor {
      .test-send {
        margin-top: 20px;

        .el-button {
          display: block;
          margin-left: auto;
          margin-right: 0;
          padding: 8px 20px;
        }
      }
    }

    .work-space {
      display: none;
    }

    .el-divider {
      background-color: #cacaca;
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
